import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import {
  ClientDietRequest,
  ClientDietResponse,
  DietGroupedResponse,
  MealOption,
} from "./MealSettings";
import { useMealOptionsStore } from "../stores/mealOptionsStore";
import { AffectedOrder } from "./AffectedOrder";

const BASE_URL = "/mashie/ClientDietSettings/diet-settings";
const mapMealOptionToClientDietRequest = (
  mealOption: MealOption
): ClientDietRequest => {
  const { mealOptionId, foodFree, toBeOrdered, disabled, dietPreferences } =
    mealOption;

  const dietPreferencesId = dietPreferences.map((p) => p.id);

  return {
    mealOptionId,
    toBeOrdered,
    disabled,
    foodFree,
    dietPreferenceIds: dietPreferencesId,
  };
};

export function useSavePrefs(clientId: string) {
  const url = `${BASE_URL}/${clientId}`;
  const store = useMealOptionsStore();
  const body = store.mealOptions.map((p) =>
    mapMealOptionToClientDietRequest(p)
  );
  const { isPending, error, mutate } = useMutation({
    mutationFn: async () => {
      await axios.post<ClientDietResponse>(url, body);
    },
  });

  return { isPending, error, savePrefs: mutate };
}

export const fetchMealOptions = async (
  clientId: string,
  categoryId: string
): Promise<ClientDietResponse> => {
  const url = `${BASE_URL}/${clientId}/${categoryId}`;
  const response = await axios.get<ClientDietResponse>(url);
  return response.data;
};

export const fetchDietPreferences = async (
  categoryId: string
): Promise<DietGroupedResponse[]> => {
  const url = `${BASE_URL}/diet-preferences/${categoryId}`;
  const response = await axios.get<DietGroupedResponse[]>(url);
  return response.data;
};

export const fetchAffectedOrders = async (
  clientId: string
): Promise<AffectedOrder[]> => {
  const url = `/mashie/ClientDietSettings/affected-orders/${clientId}`;
  const response = await axios.get(url);

  // Transform the response to AffectedOrder format
  const orders: AffectedOrder[] = [];
  response.data.forEach((dayOrder: any) => {
    dayOrder.orders?.forEach((order: any) => {
      // Parse date as local date to avoid timezone shift
      const dateStr = dayOrder.date;
      const dateParts = dateStr.split('T')[0].split('-'); // Get YYYY-MM-DD
      const orderDate = new Date(
        parseInt(dateParts[0]), // year
        parseInt(dateParts[1]) - 1, // month (0-indexed)
        parseInt(dateParts[2]) // day
      );

      orders.push({
        id: order.id,
        date: orderDate,
        name: order.name,
        mealName: order.mealName || "",
        mealOptionId: order.mealOptionId || "",
        customerName: order.customerName || "",
        isLocked: order.isLocked, // Use lock status from API
        lockTime: order.lockTime ? new Date(order.lockTime) : undefined,
        isProcessed: false,
        currentDietPreferences: order.currentDietPreferences || [],
      });
    });
  });

  return orders;
};

export function useUpdateClientOrders(clientId: string) {
  const { isPending, error, mutateAsync } = useMutation({
    mutationFn: async (lockDate?: Date) => {
      const url = `/mashie/ClientDietSettings/update-orders/${clientId}${
        lockDate ? `?lockDate=${lockDate.toISOString()}` : ""
      }`;
      await axios.post(url);
    },
  });

  return { isPending, error, updateOrders: mutateAsync };
}
